import React, { useState, useEffect, useRef, useMemo } from 'react';
import RichEditor from './RichEditor';
import FileUploader from './FileUploader';
import axios from 'axios';
import DashboardHeader from '../components/layout/DashboardHeader';
import FinnLoader from '../components/FinnLoader';
import SweetAlert from 'sweetalert2';

export default function ClientPlanSummary() {
    const [loading, setLoading] = useState(false);
    const [summary, setSummary] = useState('');
    const [summaryTitle, setSummaryTitle] = useState('')
    const [attachments, setAttachments] = useState([]);
    const [logs, setLogs] = useState([]);
    // eslint-disable-next-line
    const [currentSummary, setCurrentSummary] = useState(null);
    const [autoSaving, setAutoSaving] = useState(false);
    // eslint-disable-next-line
    const [status, setStatus] = useState('draft');
    const lastSavedSummary = useRef('');
    
    const planId = sessionStorage.getItem('profile_id');
    const adminId = sessionStorage.getItem('admin_id');

    useEffect(() => {
        if (!planId) return;

        axios.post(`https://fpapi.finnovate.in/api/get-summary`, { profile_id: planId })
            .then((res) => {
                setCurrentSummary(res.data.summary || {});
                setSummary(res.data.summary?.summary || '');
                lastSavedSummary.current = res.data.summary?.summary || ''; // keep baseline
                setSummaryTitle(res.data.summary?.executive_summary_title || '');
                setStatus(res.data.summary?.status || 'draft');
                //setAttachments(res.data.logs.attachments || []);
                setLogs(res.data.logs || []);
            })
            .catch(() => console.warn("No existing summary found."))
            .finally(() => setLoading(false));
    }, [planId]);

    const formatDateTime = (dateStr) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'long'
        }) + ', ' + date.toLocaleTimeString('en-GB', {
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const saveData = async (saveAs = 'draft', isAuto = false) => {
        if (!planId) {
            alert('Missing profile_id from sessionStorage');
            return;
        }
        
        // Skip auto-save if nothing changed
        if (isAuto && summary === lastSavedSummary.current) return;

        const formData = new FormData();
        formData.append('summary', summary);
        formData.append('status', saveAs);
        formData.append('profile_id', planId);
        formData.append('admin_id', adminId);
        formData.append('id', planId);
        formData.append('executive_summary_title', summaryTitle);

        attachments.forEach((file, index) => {
            formData.append(`attachments[${index}]`, file);
        });

        try {
            if (isAuto) setAutoSaving(true);
            await axios.post('https://fpapi.finnovate.in/api/save-executive-summary', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            if (!isAuto) {
                //alert(saveAs === 'approved' ? '✅ Approved and saved!' : '💾 Draft saved!').then();

                SweetAlert.fire({
                    title: 'Executive Summary',
                    text: saveAs === 'approved' ? '✅ Approved and saved!' : '💾 Draft saved!',
                    icon: 'success',
                    confirmButtonText: 'OK'
                }).then(() => {
                    window.location.reload();
                });

            }

            lastSavedSummary.current = summary;
        } catch (error) {
            console.error('Error saving summary:', error);
            if (!isAuto) alert('Error saving summary!');
        } finally {
            if (isAuto) setAutoSaving(false);
        }
    };

    // Auto-save every 60 seconds
    useEffect(() => {
        const interval = setInterval(() => {
            saveData('draft', true);
        }, 30000);

        return () => clearInterval(interval);
        // eslint-disable-next-line
    }, [summary, attachments]); // Watch for changes to trigger auto-save

    useEffect(() => {
        document.body.classList.add('white_bg_fp_report');
        return () => document.body.classList.remove('white_bg_fp_report');
    }, []);

    // Add state at top
    const [visibleCount, setVisibleCount] = useState(5);

    const loadMoreLogs = () => {
        setVisibleCount(prev => prev + 5); // Show 5 more each click
    };

    const [selectedLog, setSelectedLog] = useState(null);
    const [existingAttachment, setExistingAttachment] = useState([]);

    const handleLogClick = (log) => {

        console.log('log', log);

        setSelectedLog(log);
        setSummary(log.summary || "");
        setSummaryTitle(log.executive_summary_title || "");
        setStatus(log.status || "draft");
        //setAttachments(log.attachments || []);
        setExistingAttachment(log.attachments || []);
    };

    useEffect(() => {
        if (logs.length > 0 && !selectedLog) {
            handleLogClick(logs[0]); // Load latest entry only once
        }
    }, [logs, selectedLog]);

    
    return (
        <>
            <DashboardHeader />
            <div className="innercontainer mb40">
                <div className="mainwrapper">
                    {loading && <FinnLoader />}
                    <div className="planSection_wpr row">
                        {/* LEFT SIDEBAR: History */}
                        <div className="col-3 leftNav pt55 font14 fw500 color7B8">
                            <h4 className="mb-3">History</h4>
                            {logs.slice(0, visibleCount).map((log, index) => (
                                <div
                                    key={log.id}
                                    onClick={() => handleLogClick(log)}
                                    style={{
                                        marginBottom: '15px',
                                        cursor: 'pointer',
                                        backgroundColor: selectedLog?.id === log.id ? '#f0f0f0' : 'transparent',
                                        padding: '5px',
                                        borderRadius: '4px'
                                    }}
                                >
                                    <div className="color212">{formatDateTime(log.created_at)}</div>
                                    {index === 0 && <div style={{ color: 'red' }}>Current version</div>}
                                    <div>{log.admin_name || 'Unknown Admin'}</div>
                                    <hr />
                                </div>
                            ))}

                            {visibleCount < logs.length && (
                                <div
                                    className="load-more"
                                    style={{ cursor: 'pointer', color: 'red', fontWeight: 'bold' }}
                                    onClick={loadMoreLogs}
                                >
                                    LOAD MORE
                                </div>
                            )}
                        </div>


                        <div className="col-9 righttabContent pl90 pt25">
                            <div className="tab-content" id="v-pills-tabContent">
                                <div className="form_title">
                                    <div className="mt30 font21 fw600 color384">
                                        Executive Plan For {sessionStorage.getItem('full_name')}
                                    </div>
                                    <div className="font16 color626">
                                        Calculation of required amount to achieve desired financial goals.
                                    </div>
                                </div>

                                <div className="form-row mt20 mb50">
                                    <div className="col-md-12 pr20">
                                        <label className="font14 fw500">Title</label>
                                        <div className="input-group selectarrow">
                                            <input type="text" className="form-control font14 color212 fw500" name="executive_summary_title" onChange={(e) => setSummaryTitle(e.target.value)} value={summaryTitle} />
                                        </div>
                                    </div>
                                </div>

                                <div className="plan-summary-editor">
                                    <h2 className="mb-3">Executive Summary</h2>

                                    <RichEditor value={summary} onChange={setSummary} />

                                    <div style={{ marginTop: '10px' }}>
                                        {/* Pass setAttachments to FileUploader so files are stored in parent state */}
                                        <FileUploader
                                            existingFiles={useMemo(() => existingAttachment, [existingAttachment])}
                                            onFilesChange={(updatedFiles) => setAttachments(updatedFiles)}
                                        />

                                    </div>

                                    <div style={{ marginTop: '15px' }}>
                                        <button onClick={() => saveData('draft')} className="btn btn-secondary">
                                            Save Draft
                                        </button>
                                        <button
                                            onClick={() => saveData('approved')}
                                            style={{ marginLeft: '10px' }}
                                            className="btn btn-primary"
                                        >
                                            Approve & Save
                                        </button>
                                    </div>

                                    {autoSaving && <p style={{ color: 'green', marginTop: '10px' }}>Auto-saving...</p>}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
